import { ButtonRow, Form, Text } from '@ksyos/design-system';
import { isBoolean } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { ITfoDiagnosticReportCustomControlData } from '@common/validation/customControls/ophthalmology';
import { ProcessEntity } from '@common/enums/processEntity';
import {
    tfoV1DraftDiagnosticReport,
    tfoV1FinalDiagnosticReport,
} from '@common/validation/customControls/ophthalmology';
import { FormProvider } from '@frontend/components/contexts/form/FormProvider';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { getSelectedLanguage } from '@frontend/translate';
import type { IReduxState } from '../../../../../configureStore';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { diagnosticReportAddEditExaminationShowToast } from '../../utils/v1/diagnosticReportAddEditExaminationShowToast';
import { TfoV1DiagnosticReportAddEditCustomControlFields } from './TfoV1DiagnosticReportAddEditCustomControlFields';

export function TfoV1DiagnosticReportAddEdit(props: Readonly<ICustomControlDiagnosticReportAddEditProps>) {
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const episodeContext = useEpisodeContext();
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    const translate = getSelectedLanguage();

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                isFirstItem={props.isFirstItem}
                isItemOpen={true}
                organizationName={props.currentDiagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.currentDiagnosticReport?.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={getServiceLabel(
                    props.service.code,
                    'diagnosticReport',
                    translate.diagnosticReport.category_examinationReport
                )}
            />
            <Form submitOnEnter={true}>
                <Text as="h3">
                    <strong>{translate.diagnosticReport.findings}</strong>
                </Text>

                <FormProvider
                    draftValidationSchema={{ schema: tfoV1DraftDiagnosticReport }}
                    initialValues={{ ...props.diagnosticReport.customControlData }}
                    onChange={async (option) => {
                        const formattedValue =
                            option.value !== null && !isBoolean(option.value) ? `${option.value}` : option.value;
                        await props.handleChangeCustomControl({ [option.name]: formattedValue });
                        await props.validateOneCustomControl(option.name);
                    }}
                    onSubmit={async (values, saveType) => {
                        if (!saveType) {
                            return;
                        }

                        await props.handleChangeCustomControl(values);
                        await saveDiagnosticReport(saveType);
                    }}
                    validationSchema={{ schema: tfoV1FinalDiagnosticReport }}
                >
                    {(form) => (
                        <>
                            <TfoV1DiagnosticReportAddEditCustomControlFields
                                customControlData={form.values as ITfoDiagnosticReportCustomControlData}
                                showAdvisedPolicy={true}
                                showToelichting={true}
                            />

                            <ButtonRow justify="right">
                                <CancelButton
                                    cancel={props.onCancel}
                                    entity={ProcessEntity.diagnosticReport}
                                    isExisting={!!props.request.id}
                                    revertChanges={props.revertDiagnosticReportChanges}
                                    serviceCode={props.service.code}
                                    serviceType={props.service.serviceType}
                                />
                                <SaveButton
                                    buttonDisabled={isButtonDisabled}
                                    save={async () => form.handleSubmit('draft')}
                                />
                                <SaveSendButton
                                    buttonDisabled={isButtonDisabled}
                                    saveSend={async () => form.handleSubmit('final')}
                                />
                            </ButtonRow>
                        </>
                    )}
                </FormProvider>
            </Form>
        </ProcessContainer>
    );

    async function saveDiagnosticReport(saveType: 'draft' | 'final') {
        setIsButtonDisabled(true);

        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);

        if (!validatedDiagnosticReport) {
            setIsButtonDisabled(false);

            return;
        }

        const diagnosticReportId = await props.onSaveDiagnosticReport(saveType);

        await diagnosticReportAddEditExaminationShowToast(
            saveType,
            props.service,
            selectedEmploymentId,
            episodeContext.patient
        );

        setIsButtonDisabled(false);

        await props.onSave(diagnosticReportId);
    }
}
